import { useTranslation } from 'react-i18next'

import {
  DatePicker,
  DragDropFileListLocal,
  Input,
  Label,
  Select,
} from '@mercai/clever'

import { DocumentsProps } from './generate-general-information.type'

import { useSupplierWarehouseList } from '@/services'
import {
  SupplierPaymentPriorityEnum,
  SupplyOrderShipmentStatusEnum,
} from '@/types'
import { SectionHeader } from '../../../../components/section-header'
import styles from './generate-general-information.module.css'

export const GenerateGeneralInformation = ({
  values,
  setValues,
  onDropFile,
  position,
  isQuotation,
  errors,
}: DocumentsProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementComponents')

  const { data: supplierWarehouses } = useSupplierWarehouseList({
    params: {
      supplier__uuid: values?.supplier?.uuid || '',
    },
  })

  return (
    <SectionHeader
      title={`${position}. ${t('componentGenerateGeneralInformationTitle')}`}
    >
      <div className={styles['info-block']}>
        <div className={styles['info-row-content']}>
          {isQuotation && (
            <div>
              <Label required>
                {t('componentGeneralInformationPurchaseOrderCodeLabel')}
              </Label>
              <Input
                placeholder={t(
                  'componentGeneralInformationPurchaseOrderCodePlaceholder',
                )}
                value={values?.purchaseOrderCode}
                onChange={(val) =>
                  setValues((prev) => ({
                    ...prev,
                    purchaseOrderCode: val || '',
                  }))
                }
                className={styles.input}
                error={errors?.purchaseOrderCode as string}
              />
            </div>
          )}
          <div>
            <Label>
              {t('componentGeneralInformationExpectedPaymentDateLabel')}
            </Label>
            <DatePicker
              value={values?.expectedPaymentDate}
              onChange={(val) =>
                setValues((prev) => ({ ...prev, expectedPaymentDate: val }))
              }
              placeholder="DD/MM/YYYY"
              className={styles.input}
            />
          </div>
          <div>
            <Label>
              {t('componentGeneralInformationPaymentPriorityLabel')}
            </Label>
            <Select
              value={values?.paymentPriority}
              options={[
                {
                  label: t('componentGeneralInformationNoneOption'),
                  value: '',
                },
                ...Object.keys(SupplierPaymentPriorityEnum).map((key) => ({
                  label: tEnums(`supply_payment_priority_${key}`),
                  value: key,
                })),
              ]}
              onChange={(val) =>
                setValues((prev) => ({
                  ...prev,
                  paymentPriority:
                    val as keyof typeof SupplierPaymentPriorityEnum,
                }))
              }
              placeholder={t('componentGeneralInformationPlaceholder')}
              className={styles.input}
            />
          </div>

          <div>
            <Label>{t('componentGeneralInformationShipmentStatusLabel')}</Label>
            <Select
              value={values?.shipmentStatus}
              options={Object.keys(SupplyOrderShipmentStatusEnum).map(
                (key) => ({
                  label: tEnums(`supply_order_shipment_status_${key}`),
                  value: key,
                }),
              )}
              onChange={(val) =>
                setValues((prev) => ({
                  ...prev,
                  shipmentStatus:
                    val as keyof typeof SupplyOrderShipmentStatusEnum,
                }))
              }
              placeholder={t('componentGeneralInformationPlaceholder')}
              className={styles.input}
            />
          </div>

          <div>
            <Label>{t('componentGeneralInformationWarehouseLabel')}</Label>
            <Select
              value={values?.supplierWarehouseUuid}
              options={[
                {
                  label: t('componentGeneralInformationNoneOption'),
                  value: '',
                },
                ...(supplierWarehouses?.data?.results?.map((warehouse) => ({
                  label: warehouse?.name || '',
                  value: warehouse?.uuid || '',
                })) || []),
              ]}
              onChange={(val) =>
                setValues((prev) => ({
                  ...prev,
                  supplierWarehouseUuid: val,
                }))
              }
              placeholder={t('componentGeneralInformationPlaceholder')}
              className={styles.input}
            />
          </div>
        </div>
      </div>

      <div className={styles['info-block']}>
        <div>
          <Label>
            {t('componentGenerateGeneralInformationObservationLabel')}
          </Label>
          <Input
            value={values?.observation}
            onChange={(val) =>
              setValues((prev) => ({ ...prev, observation: val || '' }))
            }
            className={styles.input}
          />
        </div>
      </div>
      <div className={styles['info-block']}>
        <div className={styles['info-row-content']}>
          <div>
            <Label>
              {t('componentGenerateGeneralInformationInvoicePDFLabel')}
            </Label>
            <DragDropFileListLocal
              translations={{
                dragFile: t('uploadDrag'),
                sizeFile: t('uploadSize'),
                clickToUpload: t('uploadButton'),
              }}
              onDrop={(files) => onDropFile(files, 'invoicePDFFile')}
              data={values?.invoicePDFFile ? [values?.invoicePDFFile] : []}
            />
          </div>
          <div>
            <Label>
              {t('componentGenerateGeneralInformationInvoiceXMLLabel')}
            </Label>
            <DragDropFileListLocal
              translations={{
                dragFile: t('uploadDrag'),
                sizeFile: t('uploadSize'),
                clickToUpload: t('uploadButton'),
              }}
              onDrop={(files) => onDropFile(files, 'invoiceXMLFile')}
              data={values?.invoiceXMLFile ? [values?.invoiceXMLFile] : []}
            />
          </div>
        </div>
      </div>
    </SectionHeader>
  )
}
